import { AuthFetchWithResponse } from 'src/Types/api'
import { ApiResult } from '../../api'
import { StatusCodes } from '../../http-status-codes'
import { handleFetchError } from 'src/Libs/api/utils'
import { AuthFetchError } from '@e3dc-react/shell/Types'
import { DeviceLinksDto } from './dto'
import { DeviceLinkModel, ProductSerialnumber } from '../models'
import { deviceLinksDtoToModelTransformer } from './transformer'
import { CreateNewDeviceLinkPayload } from 'src/Libs/installations/models/device-link.model'

/**
 * fetch device links for an installation
 */
export const getDeviceLinks = async (
  authFetch: AuthFetchWithResponse,
  installationId: string | undefined
): Promise<ApiResult<DeviceLinkModel[], AuthFetchError | Error>> => {
  if (!installationId) {
    return new ApiResult<DeviceLinkModel[], AuthFetchError | Error>(null, true, new Error('Installation ID is undefined'))
  }
  try {
    const response = await authFetch<DeviceLinksDto>({
      path: `installations/${installationId}/device-links`,
      method: 'GET',
      endpoint: process.env.REACT_APP_INSTALLATIONS_ENDPOINT,
    })

    if (response.status === StatusCodes.OK) {
      const data = deviceLinksDtoToModelTransformer(response.data)
      return new ApiResult<DeviceLinkModel[], AuthFetchError | Error>(data, false)
    }

    return new ApiResult<DeviceLinkModel[], AuthFetchError | Error>(null, true)
  } catch (error) {
    return handleFetchError<DeviceLinkModel[]>(error)
  }
}

export const getProductSerialnumbers = async (
  serialnumbers: string[],
  authFetch: AuthFetchWithResponse
): Promise<ApiResult<ProductSerialnumber[], AuthFetchError | Error>> => {
  try {
    const response = await authFetch<ProductSerialnumber[]>({
      path: `devices/product-serialnos?serialnos=${encodeURIComponent(JSON.stringify(serialnumbers))}`,
      method: 'GET',
      endpoint: process.env.REACT_APP_MANUFACTURING_ENDPOINT,
    })

    if (response.status === StatusCodes.OK) {
      return new ApiResult<ProductSerialnumber[], AuthFetchError | Error>(response.data, false)
    }

    return new ApiResult<ProductSerialnumber[], AuthFetchError | Error>(null, true)
  } catch (error) {
    return handleFetchError<ProductSerialnumber[]>(error)
  }
}

export const createNewDeviceLink = async (
  authFetch: AuthFetchWithResponse,
  installationId: number,
  payload: CreateNewDeviceLinkPayload
): Promise<ApiResult<number, AuthFetchError | Error>> => {
  try {
    const response = await authFetch<number>({
      path: `installations/${installationId}/device-links`,
      method: 'POST',
      payload,
      endpoint: process.env.REACT_APP_INSTALLATIONS_ENDPOINT,
    })

    if (response.status === StatusCodes.OK || response.status === StatusCodes.CREATED) {
      return new ApiResult<number, AuthFetchError | Error>(response.data, false)
    }

    return new ApiResult<number, AuthFetchError | Error>(null, true)
  } catch (error) {
    return handleFetchError<number>(error)
  }
}

export const deleteDeviceLink = async (installationId: number, deviceLinkId: string, authFetch: AuthFetchWithResponse): Promise<ApiResult<void>> => {
  try {
    const response = await authFetch({
      endpoint: process.env.REACT_APP_INSTALLATIONS_ENDPOINT,
      path: `installations/${installationId}/device-links/${deviceLinkId}`,
      method: 'DELETE',
    })

    if (response.status !== StatusCodes.OK && response.status !== StatusCodes.NO_CONTENT) {
      return new ApiResult<void, Error | AuthFetchError>(null, true)
    }

    return new ApiResult<void>()
  } catch (error) {
    return handleFetchError<void>(error)
  }
}

export const updateDeviceLink = async (
  installationId: number,
  deviceLinkId: string,
  payload: Partial<DeviceLinkModel>,
  authFetch: AuthFetchWithResponse
): Promise<ApiResult<void>> => {
  try {
    const response = await authFetch({
      endpoint: process.env.REACT_APP_INSTALLATIONS_ENDPOINT,
      path: `installations/${installationId}/device-links/${deviceLinkId}`,
      method: 'PUT',
      payload,
    })

    if (response.status !== StatusCodes.OK && response.status !== StatusCodes.NO_CONTENT) {
      return new ApiResult<void, Error | AuthFetchError>()
    }

    return new ApiResult<void>()
  } catch (error) {
    return handleFetchError<void>(error)
  }
}

export interface UpdateEmcMiddlewarePayload {
  serialno?: string
  productSerialno?: string
}

export const updateEmcMiddleware = async (
  installationId: number,
  deviceLinkId: string,
  payload: UpdateEmcMiddlewarePayload,
  authFetch: AuthFetchWithResponse
): Promise<ApiResult<void>> => {
  try {
    const response = await authFetch({
      endpoint: process.env.REACT_APP_INSTALLATIONS_ENDPOINT,
      path: `installations/${installationId}/device-links/${deviceLinkId}/middlewares/emc`,
      method: 'PUT',
      payload,
    })

    if (response.status !== StatusCodes.OK && response.status !== StatusCodes.NO_CONTENT) {
      return new ApiResult<void, Error | AuthFetchError>(null, true)
    }

    return new ApiResult<void>()
  } catch (error) {
    return handleFetchError<void>(error)
  }
}
